import type { HTMLAttributes, ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { cn } from "@/utils";
import Label from "../Label";
import {
  inputInnerPadding,
  inputShell,
  inputSlotIcon,
} from "../input-shell";

interface FormInputProps {
  name: string;
  label?: string;
  type?: "text" | "number" | "decimal" | "email" | "password" | "date" | "time";
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoComplete?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  hint?: string;
  required?: boolean;
  size?: "sm" | "md" | "lg";
  /** Optional leading icon (Lucide component or any node) */
  iconPrefix?: ReactNode;
  /** Optional trailing icon (Lucide component or any node) */
  iconSuffix?: ReactNode;
}

export function FormInput({
  name,
  label,
  type = "text",
  placeholder,
  className,
  disabled = false,
  autoComplete,
  inputMode,
  hint,
  required = false,
  size = "md",
  iconPrefix,
  iconSuffix,
}: FormInputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const error = errors[name];
  const hasError = !!error;
  const messageId = hasError || hint ? `${name}-msg` : undefined;

  // Native number inputs can render decimal separators using the browser locale.
  // Decimal mode keeps the visible value canonical while still storing numbers.
  const registerOptions =
    type === "number"
      ? { valueAsNumber: true as const, required }
      : type === "decimal"
        ? { required, setValueAs: parseDecimalInput }
      : { required };
  const inputType = type === "decimal" ? "text" : type;
  const effectiveInputMode = inputMode ?? (type === "decimal" ? "decimal" : undefined);

  return (
    <div>
      {label && <Label htmlFor={name}>{label}</Label>}
      <div className={cn(inputShell({ size, invalid: hasError }), className)}>
        {iconPrefix && (
          <span className={inputSlotIcon} aria-hidden="true">
            {iconPrefix}
          </span>
        )}
        <input
          id={name}
          type={inputType}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          inputMode={effectiveInputMode}
          aria-invalid={hasError || undefined}
          aria-describedby={messageId}
          className={inputInnerPadding({
            hasPrefix: !!iconPrefix,
            hasSuffix: !!iconSuffix,
            size,
          })}
          {...register(name, registerOptions)}
        />
        {iconSuffix && (
          <span className={inputSlotIcon} aria-hidden="true">
            {iconSuffix}
          </span>
        )}
      </div>
      {(hasError || hint) && (
        <p
          id={messageId}
          role={hasError ? "alert" : undefined}
          className={cn(
            "mt-1.5 text-xs",
            hasError ? "text-[var(--destructive)]" : "text-[var(--muted-foreground)]"
          )}
        >
          {hasError ? (error.message as string) : hint}
        </p>
      )}
    </div>
  );
}

function parseDecimalInput(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const normalized = value.trim().replace(/,/g, ".");
  if (normalized === "") return undefined;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : normalized;
}
